import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { FiChecklist2ReportHelperService } from '../../services/fi-checklist2-report-helper.service';
import { Animations } from '@fi-shared/utils/animations';
import { ChecklistV2ReportPages } from '../../enums/fi-checklist2-report-pages.enum';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import {
    IChecklist2ReportChecklistSummary,
    IChecklist2ReportHistorySummaryDetailsData,
    IChecklistHistoryDateRangeChangeEvent,
    IChecklistReportAttentionRequiredData,
    IChecklistReportDetailsData,
    IChecklistV2FilterAvailableItems,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
    IOpenChecklistReportWidgetDetailsData,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { FiChecklist2ReportStateService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-state.service';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';

@Component({
    selector: 'fi-checklist2-report-widget-details',
    templateUrl: './fi-checklist2-report-widget-details.component.html',
    styleUrls: ['./fi-checklist2-report-widget-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-report-widget-details' },
    animations: [Animations.fade],
    standalone: false,
})
export class FiChecklist2ReportWidgetDetailsComponent implements OnInit, OnDestroy {
    @Input() currentChecklistReportPage: ChecklistV2ReportPages;
    @Output() currentChecklistReportPageChange = new EventEmitter<ChecklistV2ReportPages>();
    @Input() reportData: FiChecklist2ReportData;
    @Input() reports: IChecklistV2ReportParameter[] = [];
    @Input() widgetDetailsData: IOpenChecklistReportWidgetDetailsData;

    get loading$() {
        return this.fiChecklist2ReportDataService.getLoading$(this.widgetDetailsData.reportParam.Id);
    }
    get openChecklistsSummary(): IChecklist2ReportChecklistSummary[] {
        return this.reportData.getOpenChecklistsSummary(this.widgetDetailsData.reportParam.Id);
    }
    get availableItems(): IChecklistV2FilterAvailableItems {
        return this.reportData.availableItems;
    }

    checklistReportFilter: IChecklistV2ReportParameterFilter;
    checklistSummary: IChecklist2ReportChecklistSummary;

    constructor(
        private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService,
        private fiChecklist2ReportDataService: FiChecklist2ReportDataService,
        private fiChecklist2ReportStateService: FiChecklist2ReportStateService,
    ) {}

    ngOnInit() {
        this.subscribeOnNavigateToDashboard();

        const checklistId = this.fiChecklist2ReportStateService.getChecklistIdParam();
        const scheduleId = this.fiChecklist2ReportStateService.getScheduleIdParam();
        const filter: IChecklistV2ReportParameterFilter = {
            Guid: undefined,
        };

        if (this.fiChecklist2ReportStateService.shouldShowOpenChecklistsUnits()) {
            const selectedItem = this.openChecklistsSummary.find(
                (item) => item.ChecklistId === checklistId && item.ScheduleId === scheduleId,
            );
            if (selectedItem) {
                this.showChecklistReportDetails({
                    selectedItem: selectedItem,
                    filter: filter,
                });
            }
        } else if (this.fiChecklist2ReportStateService.shouldShowPastChecklistsUnits()) {
            const historySummary = this.reportData.getHistorySummary(this.widgetDetailsData.reportParam.Id);
            const selectedItem = historySummary.find(
                (item) => item.ChecklistId === checklistId && item.ScheduleId === scheduleId,
            );
            if (selectedItem) {
                this.showChecklistHistoryDetails({
                    checklistSummary: selectedItem,
                    filter: filter,
                });
            }
        } else if (this.fiChecklist2ReportStateService.shouldShowAttentionRequiredDetails()) {
            const deviationsSummary = this.reportData.getDeviationsSummary(this.widgetDetailsData.reportParam.Id);
            const selectedItem = deviationsSummary.find(
                (item) => item.ChecklistId === checklistId && item.ScheduleId === scheduleId,
            );
            if (selectedItem) {
                this.showAnswerDetails({
                    selectedItem: selectedItem,
                    filter: filter,
                });
            }
        }
    }

    private subscribeOnNavigateToDashboard() {
        this.fiChecklist2ReportHelperService
            .onNavigateToDashboard$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe(() => {
                this.setChecklistReportPage(ChecklistV2ReportPages.Dashboard);
                this.fiChecklist2ReportStateService.onCloseWidgetSummaryPage();
            });
    }

    showChecklistReportDetails(checklistDetailsData: IChecklistReportDetailsData) {
        this.fiChecklist2ReportHelperService.setPageNavBarVisibility(true);
        this.currentChecklistReportPage = ChecklistV2ReportPages.ChecklistReportDetails;
        this.checklistReportFilter = checklistDetailsData.filter;
        this.checklistSummary = checklistDetailsData.selectedItem;
        this.fiChecklist2ReportStateService.onShowWidgetOpenChecklistUnitsPage();
    }

    private setChecklistReportPage(newPage: ChecklistV2ReportPages) {
        this.currentChecklistReportPage = newPage;
        this.currentChecklistReportPageChange.emit(newPage);
    }

    checkpointDetailsId: number;
    showAnswerDetails(attentionRequiredData: IChecklistReportAttentionRequiredData) {
        this.fiChecklist2ReportHelperService.setPageNavBarVisibility(true);
        this.currentChecklistReportPage = ChecklistV2ReportPages.ChecklistDeviationDetails;
        this.checklistReportFilter = attentionRequiredData.filter;
        this.checkpointDetailsId = attentionRequiredData.selectedItem.CheckpointId;
        // we should pass checklistSummary to fi-checklist2-report details for showing checklist name and other checklist data
        this.checklistSummary = this.openChecklistsSummary.find((item) => {
            return (
                item.ChecklistId === attentionRequiredData.selectedItem.ChecklistId &&
                item.ScheduleId === attentionRequiredData.selectedItem.ScheduleId
            );
        });
    }

    showChecklistHistoryDetails(checklistSummaryDetailsData: IChecklist2ReportHistorySummaryDetailsData) {
        this.fiChecklist2ReportHelperService.setPageNavBarVisibility(true);
        this.currentChecklistReportPage = ChecklistV2ReportPages.ChecklistHistoryDetails;
        this.checklistReportFilter = checklistSummaryDetailsData.filter;
        this.checklistSummary = checklistSummaryDetailsData.checklistSummary;
        this.fiChecklist2ReportStateService.onShowWidgetHistoryUnitsPage();
    }

    closeWidgetDetailsPage(reportPage: ChecklistV2ReportPages) {
        this.fiChecklist2ReportHelperService.setPageNavBarVisibility(false);
        this.currentChecklistReportPage = reportPage;
        this.checklistSummary = undefined;
        this.checkpointDetailsId = undefined;
        this.fiChecklist2ReportStateService.onCloseWidgetUnitsPage();
    }

    onChecklistHistoryDateRangeChange(newReportDateRange: IChecklistHistoryDateRangeChangeEvent) {
        this.fiChecklist2ReportDataService.setNewDateRange(
            this.widgetDetailsData.reportParam.Id,
            newReportDateRange.dateRange,
        );
    }

    protected readonly ChecklistV2ReportPages = ChecklistV2ReportPages;

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
